import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { sperre } from "@/hilfe/Knopfhilfe";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, AlertCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";

const Login = () => {
    const { anmelden } = useAuth();
    const [email, setEmail] = useState("");
    const [passwort, setPasswort] = useState("");
    const [fehler, setFehler] = useState(null);
    const [busy, setBusy] = useState(false);

    const absenden = async (e) => {
        e.preventDefault();
        setFehler(null);
        setBusy(true);
        try {
            await anmelden(email.trim(), passwort);
        } catch (err) {
            const st = err?.response?.status;
            const d = err?.response?.data?.detail;
            // Das Backend antwortet auf falsche Adresse und falsches Passwort
            // absichtlich gleich. Diese Meldung nicht "verbessern" — sonst
            // verrät sie, welche Adressen einen Zugang haben.
            setFehler(
                st === 401 ? "E-Mail oder Passwort ist falsch."
                : st === 403 ? (typeof d === "string" ? d : "Dieser Zugang ist stillgelegt.")
                : st === 422 ? "Bitte eine gültige E-Mail-Adresse eingeben."
                : "Das Backend ist nicht erreichbar."
            );
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid place-items-center bg-gray-50 px-4">
            <div className="w-full max-w-sm">
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 rounded-md bg-white border border-gray-200 grid place-items-center overflow-hidden">
                        <img src="/tplex-logo.jpg" alt="Tplex" className="w-8 h-8 object-contain" />
                    </div>
                    <div className="leading-tight">
                        <div className="font-heading font-bold text-[17px] text-slate-900">Tplex</div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Mieterwechsel</div>
                    </div>
                </div>

                <form onSubmit={absenden} className="bg-white border border-gray-200 rounded-md p-6 space-y-4">
                    <div>
                        <h1 className="font-heading text-xl font-bold text-slate-900">Anmelden</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Die Verwaltung enthält Mieterdaten. Ohne Anmeldung ist nichts sichtbar.
                        </p>
                    </div>

                    <div>
                        <Label htmlFor="email">E-Mail</Label>
                        <Input id="email" type="email" value={email} autoFocus autoComplete="username"
                               onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
                    </div>
                    <div>
                        <Label htmlFor="passwort">Passwort</Label>
                        <Input id="passwort" type="password" value={passwort} autoComplete="current-password"
                               onChange={(e) => setPasswort(e.target.value)} data-testid="login-passwort" />
                    </div>

                    {fehler && (
                        <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-800"
                             data-testid="login-fehler">
                            <AlertCircle size={16} className="mt-0.5 shrink-0" />
                            <span>{fehler}</span>
                        </div>
                    )}

                    <Button type="submit" disabled={busy || !email || !passwort}
                            grund={sperre([busy, "Wird geprüft"], [!email, "Keine E-Mail eingegeben"], [!passwort, "Kein Passwort eingegeben"])}
                            className="w-full bg-slate-900 hover:bg-slate-800" data-testid="login-absenden">
                        <LogIn size={15} className="mr-1.5" />
                        {busy ? "Wird geprüft …" : "Anmelden"}
                    </Button>

                    <p className="text-[11px] text-gray-400 leading-relaxed">
                        Passwort vergessen? Ein verwaltender Zugang kann es neu setzen. Gibt es noch
                        gar keinen Zugang, wird der erste beim Start des Backends aus
                        <code className="mx-1">ADMIN_EMAIL</code> und
                        <code className="mx-1">ADMIN_PASSWORD</code> in
                        <code className="ml-1">backend/.env</code> angelegt.
                    </p>
                </form>
            </div>
        </div>
    );
};

export default Login;
